import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import type { FacetActivity } from "../multifiltering/FacetActivity";
import { FacetGroup } from "./FacetGroup";
import { Filter } from "./Filter";
import { updateStatus } from "../tracking/FeatureTracking";
import type { FeatureStatus } from "../tracking/Tracking";
import { PopularFacetValuesWrapper } from "./PopularFacetValues";
import type { FacetVisibilityAware } from "./FacetVisibilityAware";
import { element, isSearchResultPage } from "../util/Utils";
import { FacetValue } from "./FacetValue";
import type { FacetExperiment } from "./FacetExperiment";
import { getActiveVariation, isActiveVariation } from "../experiment/Experiments";
import { FilterScores } from "./FilterScores";

const FACET_CLASS = "find_facet";
const FACET_CONTENT_CLASS = "find_facet__content";
const FACET_CONTENT_SELECTOR = `.${FACET_CONTENT_CLASS}`;
const UNSELECTED_FACET_VALUES_CLASS = `find_facet__unselectedFacetValues`;
const UNSELECTED_FACET_VALUES_SELECTOR = `.${UNSELECTED_FACET_VALUES_CLASS}`;
const FACET_CONTENT_POPULAR_VALUES_CLASS = "find_facet__content--popularValues";
const SELECTED_FACET_CONTENT_VALUES_CLASS = "find_facet__content--selected";
const NON_POPULAR_FACET_CONTENT_SELECTOR = `.${UNSELECTED_FACET_VALUES_CLASS} .${FACET_CONTENT_CLASS}:not(.${FACET_CONTENT_POPULAR_VALUES_CLASS})`;
const EMPTY_FACET_CLASS = `${FACET_CLASS}--empty`;
const UNSELECTED_FACET_CONTENT_SELECTOR = `.${FACET_CONTENT_CLASS}:not(.${FACET_CONTENT_POPULAR_VALUES_CLASS}):not(.${SELECTED_FACET_CONTENT_VALUES_CLASS})`;
const SELECTED_FACET_CONTENT_SELECTOR = `.${SELECTED_FACET_CONTENT_VALUES_CLASS}`;
const SEARCH_FIELD_CLASS = "find_facet__search";
const SEARCH_FIELD_SELECTOR = `.${SEARCH_FIELD_CLASS}`;

export class Facet implements FacetVisibilityAware, FacetExperiment {
  static readonly factory = HeurekaElementFactory.byElement("fieldset", FACET_CLASS, Facet);

  constructor(readonly facet: HTMLFieldSetElement) {}

  /*                  */

  static facetId(id: string, root?: NonElementParentNode): Facet | undefined {
    return Facet.factory.byId(`facet_${id}`, root);
  }

  /*               */
  static prepareAll(fragment: DocumentFragment) {
    Facet.factory.forEach((facet: Facet) => facet.prepare(), fragment);
  }

  private prepare() {
    const oldFacet = Facet.facetId(this.id);
    if (oldFacet) {
      const oldPopularFacetValuesWrapper = oldFacet.popularFacetValuesWrapper;
      /*                                                                                           */
      if (oldPopularFacetValuesWrapper && !oldPopularFacetValuesWrapper.hasStaticPopularValues) {
        this.popularFacetValuesWrapper = oldPopularFacetValuesWrapper;
      }

      if (getActiveVariation("e3163", "StatusQuo") === "StatusQuo") {
        /*                                                        */
        const shouldHide = isActiveVariation("e3131", "ChipsOnly")
          ? this.empty === oldFacet.empty
            ? oldFacet.hidden /*                                           */
            : this.hidden
          : oldFacet.hidden;
        this.updateVisibility(shouldHide);
      }
    }
  }

  /*                       */

  get onexId(): string | undefined {
    return this.facet.dataset.onexid;
  }

  get onexGroup(): string | undefined {
    return this.facet.dataset.onexgroup;
  }

  get id(): string {
    return this.facet.id.substring("facet_".length);
  }

  get facetGroup() {
    return FacetGroup.factory.closest(this.facet);
  }

  get filter(): Filter | undefined {
    return Filter.factory.declare(this.facet.form);
  }

  private get facetVisibility() {
    return this.facet.dataset.facetVisibility;
  }

  private get noHighScoreFilter(): boolean {
    return !!this.filter && FilterScores.hasNoHighScore(this.filter.id);
  }

  private get lowScoreFilter(): boolean {
    return !!this.filter && FilterScores.hasLowScore(this.filter.id);
  }

  get mayHiddenByScore(): boolean {
    return this.facetVisibility !== "visible" && (this.facetVisibility !== "visibleByScore" || this.noHighScoreFilter);
  }

  get mayHide(): boolean {
    if (isActiveVariation("e3131", "ChipsOnly") && this.empty) {
      return true;
    }
    if (getActiveVariation("e3163", "StatusQuo") !== "StatusQuo" && isSearchResultPage()) {
      return this.mayHiddenByScore;
    }
    return this.facetVisibility !== "visible";
  }

  get mayShow(): boolean {
    if (isActiveVariation("e3131", "ChipsOnly") && this.empty) {
      return false;
    }
    if (getActiveVariation("e3163", "StatusQuo") !== "StatusQuo" && isSearchResultPage()) {
      return !this.mayHiddenByScore;
    }
    return this.facetVisibility !== "hidden";
  }

  get selected(): boolean {
    return this.facet.classList.contains("find_facet--active");
  }

  get visible(): boolean {
    return !this.hidden && this.filter?.hidden === false;
  }

  private get nextBestActionCandidate(): boolean {
    return this.facet.dataset.nextBestActionCandidate === "true";
  }

  get mayNextBestAction(): boolean {
    if (this.nextBestActionCandidate && !this.hidden) {
      const visiblePopularValues = this.popularFacetValuesWrapper?.visibleFacetValues?.length || 0;
      return visiblePopularValues > 0;
    }
    return false;
  }

  get hidden(): boolean {
    return this.facet.hidden;
  }

  set hidden(hidden) {
    this.facet.hidden = hidden;
    if (hidden) {
      this.featureStatus = this.hiddenStatus();
    }
  }

  private set featureStatus(status: FeatureStatus | undefined) {
    updateStatus(this.facet, status);
  }

  private hiddenStatus(): FeatureStatus | undefined {
    return getActiveVariation("e3163", "StatusQuo") !== "StatusQuo"
      ? this.id === "kategorie" || this.id === "kategorien" || this.lowScoreFilter
        ? "hidden"
        : undefined
      : this.id === "kategorie" || this.id === "kategorien"
        ? "hidden"
        : undefined;
  }

  get popularFacetValuesWrapper(): PopularFacetValuesWrapper | undefined {
    return PopularFacetValuesWrapper.factory.pick(undefined, this.facet);
  }

  set popularFacetValuesWrapper(wrapper: PopularFacetValuesWrapper | undefined) {
    if (wrapper) {
      const parent = element(UNSELECTED_FACET_VALUES_SELECTOR, this.facet);
      if (parent) {
        parent.insertBefore(wrapper.popularFacetValuesWrapper, parent.firstChild);
        wrapper.initialize();
      }
    }
  }

  private get selectedFacetValues() {
    const selectedFacetValuesContent = this.facet.querySelector(SELECTED_FACET_CONTENT_SELECTOR);
    return (selectedFacetValuesContent && FacetValue.factory.all(selectedFacetValuesContent)) || null;
  }

  public get visibleUnSelectedFacetValues(): FacetValue[] {
    const unselectedFacetValuesContent = this.facet.querySelector(UNSELECTED_FACET_CONTENT_SELECTOR);
    return (
      (unselectedFacetValuesContent &&
        FacetValue.factory
          .all(unselectedFacetValuesContent)
          .filter((facetValue) => !facetValue.hiddenPopularValueDuplicate)) ||
      []
    );
  }

  public get unSelectedFacetValues(): FacetValue[] {
    const unselectedFacetValuesContent = this.facet.querySelector(UNSELECTED_FACET_CONTENT_SELECTOR);
    return (unselectedFacetValuesContent && FacetValue.factory.all(unselectedFacetValuesContent)) || [];
  }

  public get allFacetValues(): FacetValue[] {
    const facetValuesContent = this.facet.querySelector(FACET_CONTENT_SELECTOR);
    return (facetValuesContent && FacetValue.factory.all(facetValuesContent)) || [];
  }

  updateVisibilityByScore() {
    this.hidden = this.mayHiddenByScore || this.empty;
    this.facetGroup?.updateVisibility(this.hidden);
    this.filter?.updateVisibility(this.hidden);
  }

  updateVisibility(shouldHide: boolean) {
    this.hidden = shouldHide ? this.mayHide : !this.mayShow;
    this.facetGroup?.updateVisibility(this.hidden);
    this.filter?.updateVisibility(this.hidden);
  }

  get facetContent() {
    return this.facet.querySelector<HTMLElement>(NON_POPULAR_FACET_CONTENT_SELECTOR);
  }

  get empty(): boolean {
    return this.facet.classList.contains(EMPTY_FACET_CLASS);
  }

  get searchField() {
    return this.facet.querySelector<HTMLElement>(SEARCH_FIELD_SELECTOR) || null;
  }

  get unselectedValuesWrapper(): HTMLElement | null {
    return this.facet.querySelector<HTMLElement>(UNSELECTED_FACET_VALUES_SELECTOR) || null;
  }

  toggleCountsVisibility(visible: boolean) {
    this.facet.classList.toggle("heureka_hideCount", !visible);
  }

  updateFeaturesPositions() {
    const visiblePopularFacetValues = this.popularFacetValuesWrapper?.visibleAndNotCheckedFacetValues;
    if (visiblePopularFacetValues && visiblePopularFacetValues.length > 0 && this.popularFacetValuesWrapper) {
      FacetValue.factory.all(this.facet).map((facetValue) => facetValue.clearPosition());
      const selectedFacetValues = this.selectedFacetValues || [];
      selectedFacetValues.forEach((facetValue, index) => {
        facetValue.position = index + 1;
      });

      const popularValuesOffset = this.popularFacetValuesWrapper?.hidden ? 0 : selectedFacetValues.length;
      visiblePopularFacetValues.forEach((popularFacetValue, index) => {
        popularFacetValue.position = index + 1 + popularValuesOffset;
      });

      const unselectedValuesOffset =
        (this.popularFacetValuesWrapper?.hidden ? 0 : visiblePopularFacetValues.length) + selectedFacetValues.length;
      this.visibleUnSelectedFacetValues?.forEach((facetValue, index) => {
        facetValue.position = index + 1 + unselectedValuesOffset;
      });
    }
  }

  get activity(): FacetActivity {
    if (this.hidden) {
      return "hidden";
    }
    if (this.selected) {
      return "active";
    }
    return "view";
  }
}
